import { useEffect, useState } from "react";
import { api } from "../api";
import { usePoll, ErrorBanner, Loading, Modal, useToast, when } from "./common";

// Réglages (refonte 20/10) : six onglets, un seul bouton Enregistrer. Les champs marqués `p` sont
// PROTÉGÉS côté serveur (POST /settings/protected, mot de passe re-saisi) ; les autres passent par
// PUT /settings. En mode réel, un desserrage d'un garde-fou renvoyé `guarded` par le PUT est rejoué
// automatiquement via le chemin protégé, après saisie du mot de passe.
const TABS = [
  { key: "general", label: "Général" },
  { key: "risk", label: "Risque" },
  { key: "copy", label: "Voie copie" },
  { key: "analysis", label: "Analyse & mises" },
  { key: "exits", label: "Sorties" },
  { key: "integrations", label: "Intégrations" },
];

const FIELDS = {
  risk: [
    { k: "max_open_positions", type: "num", label: "Positions ouvertes max", hint: "toutes voies confondues" },
    { k: "max_exposure_pct", type: "num", label: "Capital engagé max (%)", hint: "du capital (min. entre équité et pic)" },
    { k: "max_stake_pct", type: "num", step: 0.1, label: "Mise max par pari (%)" },
    { k: "max_per_event_pct", type: "num", label: "Exposition max par événement (%)", hint: "paris corrélés sur le même match" },
    { k: "max_per_category_pct", type: "num", label: "Exposition max par catégorie (%)" },
    { k: "max_per_theme_pct", type: "num", label: "Exposition max par thème (%)" },
    { k: "max_daily_loss_pct", type: "num", step: 0.5, label: "Perte journalière max (%)", hint: "coupe-circuit : pause automatique", p: true },
    { k: "max_weekly_loss_pct", type: "num", step: 0.5, label: "Perte hebdomadaire max (%)", hint: "coupe-circuit", p: true },
    { k: "max_drawdown_pct", type: "num", step: 0.5, label: "Drawdown max depuis le pic (%)", hint: "coupe-circuit, reprise automatique après 12 h", p: true },
    { k: "max_order_usdc", type: "num", label: "Plafond par ordre réel ($)", p: true },
  ],
  copy: [
    { k: "copy_lane_enabled", type: "bool", label: "Voie copie activée", hint: "reproduit les paris des meilleurs parieurs à la même part de capital" },
    { k: "copy_lane_top_n", type: "num", label: "Parieurs copiés : meilleurs par ROI" },
    { k: "copy_lane_top_n_pnl", type: "num", label: "Parieurs copiés : meilleurs par P&L" },
    { k: "copy_lane_max_open", type: "num", label: "Copies ouvertes max" },
    { k: "copy_lane_max_per_day", type: "num", label: "Copies max par 24 h" },
    { k: "copy_lane_max_price", type: "num", step: 0.01, label: "Prix max copié", hint: "au-delà, le gain potentiel est trop faible" },
    { k: "copy_lane_min_price", type: "num", step: 0.01, label: "Prix min copié", hint: "en dessous, l'outsider tenu à résolution ne gagne pas (0 = désactivé)" },
    { k: "copy_lane_day_tp_pct", type: "num", step: 0.01, label: "Objectif du jour (%)", hint: "P&L réalisé du jour ≥ N % de l'équité : plus d'entrée jusqu'à minuit UTC, sorties maintenues (0 = désactivé)" },
    { k: "copy_lane_day_sl_pct", type: "num", step: 0.5, label: "Perte max du jour, voie copie (%)", hint: "P&L réalisé du jour ≤ −N % : plus d'entrée jusqu'à minuit UTC, sorties maintenues (0 = désactivé)" },
    { k: "copy_lane_max_horizon_h", type: "num", label: "Horizon max (h)" },
    { k: "copy_lane_min_horizon_min", type: "num", label: "Horizon min (min)", hint: "écarte les marchés crypto de 5 minutes" },
    { k: "copy_lane_max_age_min", type: "num", label: "Âge max d'un print (min)", hint: "au-delà, trop tard pour copier" },
    { k: "copy_lane_max_slip_cents", type: "num", label: "Glissement max (cents)", hint: "ask accepté jusqu'à prix du parieur + N" },
    { k: "copy_lane_allow_inplay", type: "bool", label: "Copier pendant un match" },
    { k: "copy_lane_inplay_gtc_min", type: "num", label: "En match : ordre limite annulé après (min)", hint: "0 = ordre immédiat (FAK), souvent refusé pendant le match" },
    { k: "copy_lane_mirror_exits", type: "bool", label: "Sorties miroir", hint: "vendre quand le parieur vend" },
    { k: "copy_lane_exit_min_frac", type: "num", step: 0.05, label: "Sortie miroir si le parieur vend ≥ (part)", hint: "0.5 = la moitié de sa position" },
    { k: "copy_lane_floor_per_day", type: "num", label: "Copies au minimum par jour", hint: "part trop petite → mise au minimum de l'exchange. 0 = jamais" },
    { k: "copy_lane_floor_min_notional_usdc", type: "num", label: "Copie au minimum : pari du parieur ≥ ($)" },
    { k: "copy_lane_floor_max_age_min", type: "num", label: "Copie au minimum : print ≤ (min)" },
    { k: "copy_lane_llm_check", type: "bool", label: "Vérification IA avant copie", hint: "seulement au-delà de l'horizon ci-dessous" },
    { k: "copy_lane_llm_min_horizon_h", type: "num", label: "Vérification IA si échéance > (h)" },
    { k: "copy_lane_capital_frac", type: "num", step: 0.05, label: "Budget de perte de la voie (part du capital)", hint: "coupure automatique au-delà" },
    { k: "copy_lane_chain_enabled", type: "bool", label: "Lecture on-chain des remplissages", hint: "détection en ~2 s ; sinon Data API toutes les 30 s" },
  ],
  analysis: [
    { k: "kelly_fraction", type: "num", step: 0.05, label: "Fraction de Kelly", hint: "0.25 = quart de Kelly" },
    { k: "min_stake_usdc", type: "num", label: "Mise minimum ($)" },
    { k: "cost_buffer_pct", type: "num", step: 0.5, label: "Marge frais et slippage (%)" },
    { k: "edge_shrink", type: "num", step: 0.02, label: "Rappel vers le prix de marché [0–0.8]", hint: "anti-surestimation. 0 = off" },
    { k: "calib_correction", type: "bool", label: "Correction de calibration", hint: "ajuste les estimations selon nos résultats" },
    { k: "readiness_kelly_scaling", type: "bool", label: "Mises proportionnelles à la preuve d'edge" },
    { k: "min_edge_pct", type: "num", label: "Edge minimum (pts)" },
    { k: "min_ev_pct", type: "num", label: "EV minimum (%)" },
    { k: "min_confidence", type: "num", step: 0.05, label: "Confiance minimum [0–1]" },
    { k: "stability_max_div_pct", type: "num", label: "Rejet si deux estimations divergent de > (pts)", hint: "0 = off" },
    { k: "roundtable_enabled", type: "bool", label: "Table ronde entre agents avant placement" },
    { k: "min_liquidity_usdc", type: "num", label: "Liquidité minimum ($)" },
    { k: "min_volume24h_usdc", type: "num", label: "Volume 24 h minimum ($)" },
    { k: "max_days_to_resolution", type: "num", label: "Échéance max (jours)" },
    { k: "ingest_limit", type: "num", label: "Marchés ingérés par passage" },
    { k: "candidate_pool", type: "num", label: "Marchés candidats au pré-filtre" },
    { k: "exclude_derived_sport", type: "bool", label: "Exclure les marchés sportifs dérivés de l'analyse IA", hint: "spreads, totaux, buteurs…" },
    { k: "exclude_weather", type: "bool", label: "Exclure les marchés météo" },
    { k: "book_lane_enabled", type: "bool", label: "Voie cotes activée", hint: "sport pré-match : cote de bookmaker vs prix Polymarket, sans IA" },
    { k: "book_lane_min_edge_pts", type: "num", step: 0.5, label: "Voie cotes : écart minimum (pts)" },
    { k: "book_lane_max_hours", type: "num", label: "Voie cotes : coup d'envoi sous (h)" },
    { k: "learn_lane_max_per_day", type: "num", label: "Voie apprentissage : mises plancher par jour", hint: "0 = désactivée" },
    { k: "hold_to_resolution_pct", type: "num", label: "Part des paris tenus jusqu'à résolution (%)", hint: "exemptés du take-profit, alimentent la calibration" },
    { k: "auto_tune", type: "bool", label: "Auto-ajustement des paramètres", hint: "dans les bornes de sécurité" },
    { k: "learning_min_sample", type: "num", label: "Paris réglés minimum pour une recommandation" },
  ],
  exits: [
    { k: "auto_take_profit", type: "bool", label: "Take-profit automatique" },
    { k: "take_profit_price", type: "num", step: 0.01, label: "Encaisser si prix ≥", hint: "quasi résolu" },
    { k: "take_profit_edge_pct", type: "num", label: "Encaisser si edge restant < (pts)" },
    { k: "take_profit_gain_pct", type: "num", label: "Sécuriser le gain latent si ≥ (%)", hint: "0 = off" },
    { k: "terminal_exit_bid", type: "num", step: 0.001, label: "Gain maximal : vendre si bid ≥", hint: "résultat acquis, encaissé sans attendre la résolution — même pour les paris tenus. 0 = off" },
    { k: "auto_exit", type: "bool", label: "Stop-loss automatique", hint: "vendre si l'edge s'effondre" },
    { k: "auto_exit_margin_pct", type: "num", label: "Stop-loss si edge < −(pts)" },
    { k: "reconsider_margin_pct", type: "num", label: "Alerte de reconsidération si edge < −(pts)" },
    { k: "max_exit_slippage_pct", type: "num", label: "Écart max bid / prix pour vendre (%)", hint: "au-delà, la vente est différée" },
  ],
  integrations: [
    { k: "daily_summary_hour", type: "num", label: "Heure du résumé Telegram (0–23)", hint: "-1 = désactivé" },
    { k: "telegram_chat_id", type: "text", label: "Chat Telegram (id)", hint: "rempli automatiquement par /start", p: true },
    { k: "anthropic_model", type: "text", label: "Modèle Anthropic", p: true },
    { k: "polygon_signature_type", type: "select", label: "Type de signature Polymarket", opts: [["0", "0 — wallet classique (EOA)"], ["1", "1 — proxy Magic / Google"], ["2", "2 — Gnosis Safe"], ["3", "3 — deposit wallet"]], p: true },
    { k: "relayer_api_key_address", type: "text", label: "Adresse du signataire (publique)", p: true },
  ],
};
const SECRETS = [
  { k: "anthropic_api_key", label: "Clé API Anthropic" },
  { k: "serper_api_key", label: "Clé Serper.dev (recherche web)" },
  { k: "odds_api_key", label: "Clé The Odds API (cotes bookmakers)" },
  { k: "telegram_bot_token", label: "Token du bot Telegram" },
  { k: "relayer_api_key", label: "Clé API Relayer Polymarket" },
  { k: "clob_api_key", label: "CLOB apiKey (dérivé)" },
  { k: "clob_secret", label: "CLOB secret (dérivé)" },
  { k: "clob_passphrase", label: "CLOB passphrase (dérivé)" },
];
const ALL_FIELDS = Object.values(FIELDS).flat();

function normalize(f, v) {
  if (f.type === "bool") return !!v;
  if (f.type === "num") { if (v === "" || v == null) return null; const n = Number(v); return Number.isFinite(n) ? n : null; }
  if (v == null) return null;
  return String(v);
}

function Field({ f, value, dirty, onChange }) {
  const mark = dirty ? <em className="up"> · modifié</em> : null;
  if (f.type === "bool") {
    return (
      <label className="check">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
        <span>{f.label}{mark}{f.hint && <em>{f.hint}</em>}</span>
      </label>
    );
  }
  return (
    <label className="field">
      <span>{f.label}{f.p && <em title="protégé : mot de passe demandé à l'enregistrement"> 🔒</em>}{mark}{f.hint && <em> — {f.hint}</em>}</span>
      {f.type === "select"
        ? <select className="select" value={String(value ?? "")} onChange={(e) => onChange(e.target.value)}>{f.opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        : <input className={"input" + (f.type === "num" ? " mono" : "")} type={f.type === "num" ? "number" : "text"} step={f.step || (f.type === "num" ? "any" : undefined)} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />}
    </label>
  );
}

export default function Settings() {
  const { data: loaded, err, refresh } = usePoll(() => api("/settings").then((d) => d.settings), 0);
  const [tab, setTab] = useState("general");
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [pwd, setPwd] = useState(null); // { fields: {k: v}, note }
  const [password, setPassword] = useState("");
  const toast = useToast();
  useEffect(() => { if (loaded) setForm(loaded); }, [loaded]);

  if (!loaded) return err ? <ErrorBanner err={err} /> : <Loading />;
  const s = loaded;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const dirty = (f) => { const nv = normalize(f, form[f.k]); return nv !== null && nv !== normalize(f, s[f.k]); };
  const changed = ALL_FIELDS.filter(dirty);
  const perTab = Object.fromEntries(Object.entries(FIELDS).map(([k, fs]) => [k, fs.filter(dirty).length]));

  async function save() {
    if (!changed.length) return;
    const plain = {}, prot = {};
    for (const f of changed) (f.p ? prot : plain)[f.k] = normalize(f, form[f.k]);
    setBusy(true);
    try {
      let guardedKeys = [];
      if (Object.keys(plain).length) {
        const d = await api("/settings", { method: "PUT", body: plain });
        if (d.clamped?.length) toast("Borné : " + d.clamped.map((c) => `${c.field} → ${c.to}`).join(", "), "down");
        guardedKeys = (d.guarded || []).map((g) => String(g).split(":")[0].trim()).filter((k) => k in plain);
        const saved = Object.keys(plain).filter((k) => !guardedKeys.includes(k) && !(d.ignored || []).includes(k));
        if (saved.length) toast(`${saved.length} réglage${saved.length > 1 ? "s" : ""} enregistré${saved.length > 1 ? "s" : ""}.`);
      }
      for (const k of guardedKeys) prot[k] = plain[k];
      if (Object.keys(prot).length) {
        setPwd({ fields: prot, note: guardedKeys.length ? "Desserrage d'un garde-fou en mode réel : mot de passe requis." : "Réglages protégés : mot de passe requis." });
      } else refresh();
    } catch (e) { toast(e.message, "down"); }
    finally { setBusy(false); }
  }
  async function saveProtected() {
    if (!password) { toast("Mot de passe requis.", "down"); return; }
    setBusy(true);
    try {
      const d = await api("/settings/protected", { method: "POST", body: { password, ...pwd.fields } });
      const refused = d?.refused || [], clamped = (d?.clamped || []).map((c) => `${c.field} borné → ${c.to}`);
      if (refused.length || clamped.length) toast([...refused, ...clamped].join(" · "), "down");
      else toast(`${(d?.changed || []).length} réglage(s) protégé(s) enregistré(s).`);
      setPwd(null); setPassword(""); refresh();
    } catch (e) { toast(e.message, "down"); }
    finally { setBusy(false); }
  }

  return (
    <div>
      <div className="page-head">
        <div><h1>Réglages</h1><div className="sub">Politique de paris, garde-fous, voie copie et intégrations.</div></div>
      </div>
      <ErrorBanner err={err} />
      <div className="settings-layout">
        <nav className="settings-nav">
          {TABS.map((t) => <button key={t.key} className={tab === t.key ? "active" : ""} onClick={() => setTab(t.key)}>{t.label}{perTab[t.key] ? <span className="n">{perTab[t.key]}</span> : null}</button>)}
        </nav>
        <div>
          {tab === "general" && <General s={s} onChanged={refresh} />}
          {tab === "risk" && <Panel title="Garde-fous" hint="Les coupe-circuits mettent le système en pause ; les plafonds refusent les nouveaux paris. En mode réel, un desserrage demande le mot de passe."><Fields keys="risk" form={form} s={s} set={set} dirty={dirty} /></Panel>}
          {tab === "copy" && <Panel title="Voie copie" hint="Les parieurs suivis sont choisis automatiquement parmi les plus rentables (page Système). Les plafonds globaux de la page Risque s'appliquent aussi."><Fields keys="copy" form={form} s={s} set={set} dirty={dirty} /></Panel>}
          {tab === "analysis" && <Panel title="Analyse IA et mises" hint="Sélection des marchés analysés par les agents, seuils de décision, taille des mises et voies secondaires."><Fields keys="analysis" form={form} s={s} set={set} dirty={dirty} /></Panel>}
          {tab === "exits" && <Panel title="Sorties" hint="Quand vendre une position avant sa résolution."><Fields keys="exits" form={form} s={s} set={set} dirty={dirty} /></Panel>}
          {tab === "integrations" && (
            <>
              <Connections />
              <Panel title="Paramètres" ><Fields keys="integrations" form={form} s={s} set={set} dirty={dirty} /></Panel>
              <Secrets s={s} onChanged={refresh} />
            </>
          )}
          {tab !== "general" && (
            <div className="savebar">
              <span className="muted small">{changed.length ? `${changed.length} modification${changed.length > 1 ? "s" : ""} non enregistrée${changed.length > 1 ? "s" : ""}` : "Aucune modification"}</span>
              <button className="btn btn-quiet btn-sm" onClick={() => setForm(s)} disabled={busy || !changed.length}>Annuler</button>
              <button className="btn btn-primary" onClick={save} disabled={busy || !changed.length}>{busy ? "Enregistrement…" : "Enregistrer"}</button>
            </div>
          )}
        </div>
      </div>
      {pwd && (
        <Modal title="Mot de passe requis" onCancel={() => { setPwd(null); setPassword(""); refresh(); }} onConfirm={saveProtected} confirmLabel="Enregistrer" busy={busy}>
          <p className="muted small">{pwd.note}</p>
          <ul className="mono small" style={{ margin: "0 0 12px", paddingLeft: 18 }}>{Object.entries(pwd.fields).map(([k, v]) => <li key={k}>{k} → {String(v)}</li>)}</ul>
          <input className="input" type="password" autoComplete="current-password" placeholder="Mot de passe" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveProtected()} autoFocus />
        </Modal>
      )}
    </div>
  );
}

function Panel({ title, hint, children }) {
  return (
    <div className="card card-pad" style={{ marginBottom: 14 }}>
      <div className="section-head" style={{ marginBottom: 14 }}><div><h2>{title}</h2>{hint && <div className="hint">{hint}</div>}</div></div>
      {children}
    </div>
  );
}
function Fields({ keys, form, s, set, dirty }) {
  return <div className="fields">{FIELDS[keys].map((f) => <Field key={f.k} f={f} value={form[f.k]} dirty={dirty(f)} onChange={(v) => set(f.k, v)} />)}</div>;
}

// ——— Général : état courant, mode réel, autonomie, pause ———
function General({ s, onChanged }) {
  const live = s.mode === "live";
  const [open, setOpen] = useState(null); // "mode" | "pilot"
  return (
    <>
      <div className="card card-pad" style={{ marginBottom: 14 }}>
        <div className="section-head"><h2>État courant</h2></div>
        <dl className="kv" style={{ gap: "8px 18px" }}>
          <dt>Mode</dt><dd>{live ? <span className="up">Réel</span> : "Simulation"}{live && (s.live_trading_armed ? " · ordres placés automatiquement" : " · non armé, signaux seulement")}</dd>
          <dt>Pilotage</dt><dd>{{ manual: "Manuel (validation de chaque pari)", semi: "Semi-automatique", auto: "Automatique" }[s.autonomy] || s.autonomy}{s.live_autopilot ? " · pilotage automatique en réel" : ""}</dd>
          <dt>Pause</dt><dd>{s.paused ? <span className="down">En pause{s.pause_reason ? ` — ${s.pause_reason}` : ""}</span> : <span className="up">non</span>}</dd>
          <dt>Plafond par ordre</dt><dd className="mono">{s.max_order_usdc != null ? `${Number(s.max_order_usdc).toFixed(0)} $` : "—"}</dd>
          <dt>Cash suivi</dt><dd className="mono">{live ? Number(s.live_bankroll ?? 0).toFixed(2) : Number(s.sim_bankroll ?? 0).toFixed(2)} $</dd>
          {s.live_armed_at && <><dt>Armé le</dt><dd>{when(s.live_armed_at)}{/@/.test(String(s.live_armed_by || "")) ? ` par ${s.live_armed_by}` : ""}</dd></>}
        </dl>
        <div className="row wrap" style={{ gap: 8, marginTop: 14 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setOpen(open === "mode" ? null : "mode")}>Changer de mode / armer</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setOpen(open === "pilot" ? null : "pilot")}>Pilotage et pause</button>
        </div>
      </div>
      {open === "mode" && <ArmLive s={s} onChanged={() => { onChanged(); setOpen(null); }} />}
      {open === "pilot" && <Pilot s={s} onChanged={() => { onChanged(); setOpen(null); }} />}
    </>
  );
}

function PasswordRow({ password, setPassword }) {
  return <label className="field"><span>Mot de passe</span><input className="input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} /></label>;
}

function ArmLive({ s, onChanged }) {
  const live = s.mode === "live";
  const [mode, setMode] = useState(live ? (s.live_trading_armed ? "armed" : "signals") : "simulation");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const toast = useToast();
  async function submit(e) {
    e.preventDefault();
    if (!password) { toast("Mot de passe requis.", "down"); return; }
    setBusy(true); setRes(null);
    try {
      let d;
      if (mode === "armed") d = await api("/settings/arm-live", { method: "POST", body: { password } });
      else if (mode === "signals") {
        if (!live) { setRes({ ok: false, message: "« Réel non armé » s'obtient en désarmant depuis « Réel armé »." }); setBusy(false); return; }
        d = await api("/settings/disarm-live", { method: "POST", body: { toSimulation: false } });
      } else d = await api("/settings/disarm-live", { method: "POST", body: { toSimulation: true } });
      const missing = Array.isArray(d?.missing) ? d.missing.map((x) => (typeof x === "string" ? x : x?.label || x?.message || JSON.stringify(x))) : [];
      const ok = d?.ok !== false && !missing.length;
      setRes({ ok, message: d?.message || (ok ? "Mode mis à jour." : "Prérequis manquants — rien n'a changé."), missing });
      if (ok) { setPassword(""); toast("Mode mis à jour."); onChanged(); }
    } catch (e) { setRes({ ok: false, message: e.message, missing: e.data?.missing || [] }); }
    finally { setBusy(false); }
  }
  return (
    <form className="card card-pad" style={{ marginBottom: 14 }} onSubmit={submit}>
      <div className="section-head"><div><h2>Mode de fonctionnement</h2><div className="hint">Protégé par mot de passe. Le serveur vérifie les prérequis (clés, signature, capital, géolocalisation).</div></div></div>
      <div className="fields">
        <label className="field"><span>Mode cible</span>
          <select className="select" value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="simulation">Simulation — aucun argent engagé</option>
            <option value="signals">Réel non armé — signaux seulement</option>
            <option value="armed">Réel armé — ordres placés sur Polymarket</option>
          </select></label>
        <PasswordRow password={password} setPassword={setPassword} />
      </div>
      <div className="row center" style={{ gap: 10 }}>
        <button type="submit" className={"btn btn-sm " + (mode === "armed" ? "btn-danger" : "btn-primary")} disabled={busy}>{busy ? "…" : mode === "armed" ? "Armer le réel" : "Appliquer"}</button>
        {res && <span className={res.ok ? "up small" : "down small"}>{res.message}{res.missing?.length ? " : " + res.missing.join(", ") : ""}</span>}
      </div>
    </form>
  );
}

function Pilot({ s, onChanged }) {
  const [f, setF] = useState({ autonomy: s.autonomy || "manual", live_autopilot: !!s.live_autopilot, paused: !!s.paused, max_order_usdc: s.max_order_usdc ?? "" });
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const set = (k, v) => setF((o) => ({ ...o, [k]: v }));
  async function submit(e) {
    e.preventDefault();
    const body = { password };
    if (f.autonomy !== (s.autonomy || "manual")) body.autonomy = f.autonomy;
    if (f.live_autopilot !== !!s.live_autopilot) body.live_autopilot = f.live_autopilot;
    if (f.paused !== !!s.paused) body.paused = f.paused;
    if (String(f.max_order_usdc) !== String(s.max_order_usdc ?? "")) body.max_order_usdc = f.max_order_usdc;
    if (Object.keys(body).length === 1) { toast("Aucun changement.", "down"); return; }
    if (!password) { toast("Mot de passe requis.", "down"); return; }
    setBusy(true);
    try {
      const d = await api("/settings/protected", { method: "POST", body });
      if (d?.refused?.length) toast(d.refused.join(" · "), "down"); else toast("Enregistré.");
      setPassword(""); onChanged();
    } catch (e) { toast(e.message, "down"); }
    finally { setBusy(false); }
  }
  return (
    <form className="card card-pad" style={{ marginBottom: 14 }} onSubmit={submit}>
      <div className="section-head"><div><h2>Pilotage et pause</h2><div className="hint">Protégé par mot de passe.</div></div></div>
      <div className="fields">
        <label className="field"><span>Autonomie</span>
          <select className="select" value={f.autonomy} onChange={(e) => set("autonomy", e.target.value)}>
            <option value="manual">Manuel — chaque pari est validé à la main</option>
            <option value="semi">Semi-automatique — auto sous un plafond</option>
            <option value="auto">Automatique</option>
          </select></label>
        <label className="field"><span>Plafond par ordre réel ($)</span><input className="input mono" type="number" min="1" step="1" value={f.max_order_usdc} onChange={(e) => set("max_order_usdc", e.target.value)} /></label>
        <label className="check"><input type="checkbox" checked={f.live_autopilot} onChange={(e) => set("live_autopilot", e.target.checked)} /><span>Pilotage automatique en réel<em>même comportement qu'en simulation sur le solde réel ; les coupe-circuits de perte restent actifs</em></span></label>
        <label className="check"><input type="checkbox" checked={f.paused} onChange={(e) => set("paused", e.target.checked)} /><span>En pause<em>coupe-circuit manuel : aucun nouvel ordre, les sorties restent possibles</em></span></label>
        <PasswordRow password={password} setPassword={setPassword} />
      </div>
      <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>{busy ? "…" : "Enregistrer"}</button>
    </form>
  );
}

function Secrets({ s, onChanged }) {
  const [vals, setVals] = useState({});
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const filled = SECRETS.filter((f) => (vals[f.k] || "").trim() !== "");
  async function submit(e) {
    e.preventDefault();
    if (!filled.length) return;
    if (!password) { toast("Mot de passe requis.", "down"); return; }
    setBusy(true);
    const body = { password };
    for (const f of filled) body[f.k] = vals[f.k].trim();
    try { await api("/settings/secrets", { method: "PUT", body }); toast(`Secret${filled.length > 1 ? "s" : ""} enregistré${filled.length > 1 ? "s" : ""}.`); setVals({}); setPassword(""); onChanged(); }
    catch (e) { toast(e.message, "down"); }
    finally { setBusy(false); }
  }
  return (
    <form className="card card-pad" style={{ marginBottom: 14 }} onSubmit={submit}>
      <div className="section-head"><div><h2>Clés et secrets</h2><div className="hint">Jamais affichés. Ne saisir que ce qui change. La clé privée Polygon reste dans le fichier .env du serveur.</div></div></div>
      <div className="fields">
        {SECRETS.map((f) => (
          <label key={f.k} className="field">
            <span>{f.label} <em className={s[`${f.k}_set`] ? "up" : ""}>— {s[`${f.k}_set`] ? "configuré" : "non configuré"}</em></span>
            <input className="input mono" type="password" autoComplete="new-password" placeholder={s[`${f.k}_set`] ? "•••••••• (laisser vide pour conserver)" : ""} value={vals[f.k] || ""} onChange={(e) => setVals((v) => ({ ...v, [f.k]: e.target.value }))} />
          </label>
        ))}
        <PasswordRow password={password} setPassword={setPassword} />
      </div>
      <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !filled.length}>{busy ? "…" : `Enregistrer${filled.length ? ` (${filled.length})` : ""}`}</button>
    </form>
  );
}

function Connections() {
  const { data: c, refresh } = usePoll(() => api("/settings/connections").then((d) => d.connections), 0);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  async function testTelegram() {
    setBusy(true);
    try { await api("/settings/telegram/test", { method: "POST" }); toast("Message de test envoyé sur Telegram."); }
    catch (e) { toast(e.message, "down"); } finally { setBusy(false); }
  }
  const Line = ({ ok, label, detail }) => (
    <div className="row-between" style={{ padding: "7px 0", borderBottom: "1px solid var(--line)" }}>
      <span className="row center" style={{ gap: 8 }}><span className={"dot " + (ok === true ? "dot-up" : ok === false ? "dot-down" : "")} />{label}</span>
      <span className="muted mono small right">{detail}</span>
    </div>
  );
  return (
    <div className="card card-pad" style={{ marginBottom: 14 }}>
      <div className="section-head"><h2>Connexions</h2><div className="row" style={{ gap: 8 }}><button type="button" className="btn btn-quiet btn-sm" onClick={refresh}>Rafraîchir</button><button type="button" className="btn btn-ghost btn-sm" onClick={testTelegram} disabled={busy}>Tester Telegram</button></div></div>
      {!c && <p className="muted small">Vérification…</p>}
      {c && (
        <div>
          <Line ok={c.gamma?.ok} label="Polymarket — marchés (Gamma)" detail={c.gamma?.note} />
          <Line ok={c.geo?.blocked === false} label="Sortie réseau (géoblocage Polymarket)" detail={c.geo?.blocked == null ? "?" : (c.geo.blocked ? "BLOQUÉE" : "autorisée") + " · " + c.geo.country} />
          <Line ok={c.llm?.claudeCli || c.llm?.anthropicKey} label="IA (CLI Claude / API Anthropic)" detail={c.llm?.claudeCli ? "CLI Claude" : c.llm?.anthropicKey ? "clé API" : "aucun backend"} />
          <Line ok={c.telegram?.ok ? (c.telegram?.chatRegistered ? true : null) : (c.telegram?.tokenSet ? false : null)} label="Telegram"
            detail={!c.telegram?.tokenSet ? "pas de token" : !c.telegram?.ok ? "token invalide" : (c.telegram?.bot ? "@" + c.telegram.bot : "ok") + (c.telegram?.chatRegistered ? " · chat enregistré" : " · envoyez /start au bot")} />
          <Line ok={c.serper?.set ? true : null} label="Recherche web (Serper.dev)" detail={c.serper?.set ? "configurée" : "optionnelle"} />
          <Line ok={c.clob?.executionReady ? true : null} label="Exécution réelle (clé, L2, funder)" detail={c.clob?.executionReady ? "prête" : c.clob?.privateKeySet ? "clé OK · L2 à dériver" : "non configurée"} />
          <Line ok={c.clob?.relayerKeySet ? true : null} label="Relayer (transactions sans gas)" detail={c.clob?.relayerKeySet ? "clé OK" : "optionnel"} />
        </div>
      )}
    </div>
  );
}
